import { useEffect, useState } from "react";
import Link from "next/link";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiDelete, apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Product = { id: string; title: string; price: number; stock: number; status?: string; category: string };

export function VendorProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);

  async function load() {
    const data = await apiGet<{ products: Product[] }>("/api/vendors/me/products");
    setProducts(data?.products ?? []);
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(id: string) {
    try {
      await apiDelete(`/api/vendors/me/products/${id}`);
      toast.success("Product removed");
      void load();
    } catch {
      toast.error("Could not delete product");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Products</h2>
        <Link href="/vendor/products/new">
          <Button>Add product</Button>
        </Link>
      </div>
      <div className="amazon-card divide-y divide-slate-100 dark:divide-white/10">
        {products.length === 0 && <p className="p-6 text-sm text-slate-500">No products yet.</p>}
        {products.map((p) => (
          <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-bold line-clamp-2">{p.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {p.category} · Stock {p.stock} · {formatPrice(p.price)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2.5">
              <Badge tone={p.status === "APPROVED" ? "success" : "prime"}>{p.status ?? "APPROVED"}</Badge>
              <Link href={`/vendor/products/${p.id}`} className="text-sm font-bold text-amazon-teal">
                Edit
              </Link>
              <button onClick={() => remove(p.id)} className="text-sm font-bold text-red-600">
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
